import { format } from "date-fns";
import {
  ColorSpaceIdentifier,
  FlashSettingIdentifier,
  MeteringModeIdentifier,
  OrientationIdentifier,
  WhiteBalanceIdentifier,
} from "./image_metadata";

export interface ImageMetadataEntry {
  key: string;
  label: string;
  value: string | number | boolean | null;
}

export const IMAGE_METADATA_ORDER_COMPARATOR = (
  a: ImageMetadataEntry,
  b: ImageMetadataEntry,
) => {
  const aIndex = IMAGE_METADATA_ORDERING.indexOf(a.key);
  const bIndex = IMAGE_METADATA_ORDERING.indexOf(b.key);
  return aIndex - bIndex;
};

export const IMAGE_METADATA_ORDERING = [
  "description",
  "copyright",
  "keywords",
  "date_time_original",
  "x_resolution",
  "y_resolution",
  "resolution_unit",
  "color_space",
  "orientation",
  "title",
  "headline",
  "caption",
  "subject",
  "creator",
  "artist",
  "credits",
  "rights_usage_terms",
  "camera_make",
  "camera_model",
  "lens_model",
  "focal_length",
  "exposure_time",
  "megapixels",
  "aperture_value",
  "iso_speed",
  "metering_mode",
  "flash",
  "white_balance",
];

export const IMAGE_METADATA_KEY_TO_LABELS: Record<
  string,
  {
    label: string;
    renderer?: (value: string | number) => string | number;
  }
> = {
  aperture_value: {
    label: "Aperture value",
  },
  artist: {
    label: "Artist",
  },
  date_time_original: {
    label: "Date taken",
    renderer: (value: string | number) => {
      return format(
        new Date(value.toString().replace("Z", "")),
        "M/dd/yyyy hh:mm a",
      );
    },
  },
  camera_make: {
    label: "Camera brand",
  },
  camera_model: {
    label: "Camera model",
  },
  caption: {
    label: "Caption",
  },
  color_space: {
    label: "Color profile",
    renderer: (value: string | number) => {
      if (typeof value === "number") {
        return ColorSpaceIdentifier[value] ?? "Unknown";
      }
      return "Unknown";
    },
  },
  copyright: {
    label: "Copyright",
  },
  creator: {
    label: "Creator",
  },
  credits: {
    label: "Credits",
  },
  description: {
    label: "Description",
  },
  exposure_time: {
    label: "Shutter speed",
    renderer: (value: string | number) => `${value} s`,
  },
  flash: {
    label: "Flash",
    renderer: (value: string | number) => {
      if (typeof value === "number") {
        return FlashSettingIdentifier[value] ?? "Unknown";
      }
      return "Unknown";
    },
  },
  focal_length: {
    label: "Focal length",
  },
  headline: {
    label: "Headline",
  },
  iso_speed: {
    label: "ISO",
  },
  keywords: {
    label: "Keywords",
  },
  lens_model: {
    label: "Lens",
  },
  licensor_url: {
    label: "Licensor URL",
  },
  // megapixels: {
  //   label: "Megapixels",
  // },
  orientation: {
    label: "Orientation",
    renderer: (value: string | number) => {
      if (typeof value === "number") {
        return OrientationIdentifier[value] ?? "Unknown";
      }
      return "Unknown";
    },
  },
  rights_usage_terms: {
    label: "Usage rights",
  },
  subject: {
    label: "Subject",
  },
  title: {
    label: "Title",
  },
  white_balance: {
    label: "White balance",
    renderer: (value: string | number) => {
      if (typeof value === "number") {
        return WhiteBalanceIdentifier[value] ?? "Unknown";
      }
      return "Unknown";
    },
  },
  metering_mode: {
    label: "Metering",
    renderer: (value: string | number) => {
      if (typeof value === "number") {
        return MeteringModeIdentifier[value] ?? "Unknown";
      }
      return "Unknown";
    },
  },
};
